/**
 * AddWellModal — Task 25.4 (DEC-115). Manually add a well not in OCC/RRC data.
 * Includes a searchable operator picker (reuses the platform-operators list API).
 */
import * as React from 'react';
import { toast } from 'sonner';
import { useDebouncedValue } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { FormRow, fieldStyle, labelStyle, subText } from '../../ui/bits';
import { useOperators } from '../operators/hooks';
import { useCreateWell } from './hooks';
import { ADD_WELL_TYPE_OPTIONS } from './well-type';

export const STATES = ['OK', 'TX', 'WY', 'NM', 'ND'] as const;
// Well types offered when adding a well: one "Disposal" (stored SWD), see well-type.ts.
export const WELL_STATUSES = ['AC', 'PA'] as const;

export function OperatorPicker({
  operatorId,
  operatorName,
  onSelect,
}: {
  operatorId: string;
  operatorName: string;
  onSelect: (id: string, name: string) => void;
}): React.JSX.Element {
  const [search, setSearch] = React.useState('');
  const [open, setOpen] = React.useState(false);
  // OPEN-194: one request after a pause in typing, not one per keystroke; and none at all until
  // something is typed (the results only show then, and the unfiltered list is the whole catalog).
  const q = useDebouncedValue(search.trim());
  const { data } = useOperators({ ...(q !== '' && { q }), sort: 'name' }, q !== '');
  const results = (data?.operators ?? []).slice(0, 8);

  if (operatorId !== '') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>{operatorName}</span>
        <Button type="button" variant="outline" onClick={(): void => onSelect('', '')}>
          Change
        </Button>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <input
        value={search}
        onChange={(e): void => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={(): void => setOpen(true)}
        placeholder="Search operators…"
        style={fieldStyle}
      />
      {open && search.trim() !== '' && (
        <div
          style={{
            position: 'absolute',
            zIndex: 10,
            top: '44px',
            left: 0,
            right: 0,
            maxHeight: '200px',
            overflowY: 'auto',
            backgroundColor: 'var(--color-bg-tertiary)',
            border: '1px solid var(--color-border-secondary)',
            borderRadius: '10px',
          }}
        >
          {results.length === 0 && <div style={{ ...subText, padding: '10px' }}>No matches.</div>}
          {results.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={(): void => {
                onSelect(o.id, o.displayName ?? o.name);
                setOpen(false);
                setSearch('');
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                background: 'none',
                border: 'none',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {o.name}
              {o.state !== null && (
                <span style={{ ...subText, marginLeft: '6px' }}>· {o.state}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export interface AddWellModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreated: (id: string) => void;
}

export function AddWellModal({
  open,
  onOpenChange,
  onCreated,
}: AddWellModalProps): React.JSX.Element {
  const [apiNumber, setApiNumber] = React.useState('');
  const [wellName, setWellName] = React.useState('');
  const [operatorId, setOperatorId] = React.useState('');
  const [operatorName, setOperatorName] = React.useState('');
  const [state, setState] = React.useState<string>('OK');
  const [county, setCounty] = React.useState('');
  const [wellType, setWellType] = React.useState<string>('OIL');
  const [wellStatus, setWellStatus] = React.useState<string>('AC');
  const [lat, setLat] = React.useState('');
  const [lng, setLng] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setApiNumber('');
    setWellName('');
    setOperatorId('');
    setOperatorName('');
    setState('OK');
    setCounty('');
    setWellType('OIL');
    setWellStatus('AC');
    setLat('');
    setLng('');
    setError(null);
  }, [open]);

  const create = useCreateWell((id) => {
    toast.success('Well added');
    onOpenChange(false);
    onCreated(id);
  });

  const save = (): void => {
    if (apiNumber.trim() === '') return setError('API number is required.');
    if (wellName.trim() === '') return setError('Well name is required.');
    if (operatorId === '') return setError('Select an operator.');
    const latNum = lat.trim() === '' ? undefined : Number(lat);
    const lngNum = lng.trim() === '' ? undefined : Number(lng);
    if (latNum !== undefined && Number.isNaN(latNum)) return setError('Latitude must be a number.');
    if (lngNum !== undefined && Number.isNaN(lngNum))
      return setError('Longitude must be a number.');
    setError(null);
    create.mutate(
      {
        apiNumber: apiNumber.trim(),
        wellName: wellName.trim(),
        operatorId,
        state,
        ...(county.trim() !== '' && { county: county.trim() }),
        wellType,
        wellStatus,
        ...(latNum !== undefined && { wellheadLat: latNum }),
        ...(lngNum !== undefined && { wellheadLng: lngNum }),
        source: 'MANUAL',
      },
      { onError: () => setError('Could not add well — the API number may already exist.') },
    );
    return undefined;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add well</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormRow label="API number" htmlFor="w-api" required>
            <input
              id="w-api"
              value={apiNumber}
              onChange={(e): void => setApiNumber(e.target.value)}
              placeholder="35-017-00000"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Well name" htmlFor="w-name" required>
            <input
              id="w-name"
              value={wellName}
              onChange={(e): void => setWellName(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Operator" required>
            <OperatorPicker
              operatorId={operatorId}
              operatorName={operatorName}
              onSelect={(id, name): void => {
                setOperatorId(id);
                setOperatorName(name);
              }}
            />
          </FormRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <FormRow label="State" htmlFor="w-state">
              <select
                id="w-state"
                value={state}
                onChange={(e): void => setState(e.target.value)}
                style={fieldStyle}
              >
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </FormRow>
            <FormRow label="County" htmlFor="w-county">
              <input
                id="w-county"
                value={county}
                onChange={(e): void => setCounty(e.target.value.toUpperCase())}
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="Well type" htmlFor="w-type">
              <select
                id="w-type"
                value={wellType}
                onChange={(e): void => setWellType(e.target.value)}
                style={fieldStyle}
              >
                {ADD_WELL_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormRow>
            <FormRow label="Well status" htmlFor="w-status">
              <select
                id="w-status"
                value={wellStatus}
                onChange={(e): void => setWellStatus(e.target.value)}
                style={fieldStyle}
              >
                {WELL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </FormRow>
            <FormRow label="GPS latitude" htmlFor="w-lat">
              <input
                id="w-lat"
                value={lat}
                onChange={(e): void => setLat(e.target.value)}
                placeholder="35.4504"
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="GPS longitude" htmlFor="w-lng">
              <input
                id="w-lng"
                value={lng}
                onChange={(e): void => setLng(e.target.value)}
                placeholder="-97.5164"
                style={fieldStyle}
              />
            </FormRow>
          </div>
          <p style={{ ...subText, textTransform: 'none' }}>Source: MANUAL</p>
          {error !== null && (
            <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={create.isPending}
            onClick={save}
            style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
          >
            {create.isPending ? 'Adding…' : 'Add Well'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
